import { Box, Grid, MenuItem, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import { ConfirmationDialog } from "../../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import LoadingSpinner from "../../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { today } from "../../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { VariationSelection } from "../../../../../common/types/apiTypes";
import { fetchTESVariations } from "../../../../orders/orderAPI";
import { TesVariationForm } from "../../../../orders/orderTesVariation/TESVariationForm";
import { VariationElement } from "../../../../orders/types/tesVariationTypes";
import { changeAssignmentTesSel } from "../../../../talents/talentsSlice";
import useNotification from "../../../../../hooks/useNotification";
import theme from "../../../../../theme";

type Props = {
  canEdit: boolean;
};

const TESContract = ({ canEdit }: Props) => {
  const [elements, setElements] = useState<VariationElement[]>([]);
  const [edit, setEdit] = useState(false);
  const [isValidTesVariations, setIsValidTesVariations] = useState(false);
  const [tesVariationsLoading, setTesVariationsLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [isConfirmEditDialogOpen, setIsConfirmEditDialogOpen] = useState(false);
  const [successfulSubmit, setSuccesfulSubmit] = useState<
    boolean | undefined
  >();

  const [defaultValues, setDefaultValues] = useState({
    tes: {
      name: "",
      id: "",
    },
    tesVariation: [] as VariationSelection[],
  });

  const { assignment, localized } = useAppSelector((state) => ({
    assignment: state.talents.assignment,
    localized: state.localized,
  }));

  const translatedTesNames = localized.tes;

  const { t } = useTranslation();
  const { sendNotification } = useNotification();
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { isDirty, errors },
    reset: resetForm,
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  const tes = getValues("tes");

  // Sets the tesId and tesVariation from the redux state to the form,
  // because we may not have assignment details ready for form defaultValues
  // when the component is rendered
  useEffect(() => {
    let existingDefaultValues = {
      tes: {
        name: "",
        id: "",
      },
      tesVariation: [] as VariationSelection[],
    };

    if (assignment.details.tesId) {
      const tesObj = {
        name:
          translatedTesNames[assignment.details.tesId] ||
          assignment.details.tesId,
        id: assignment.details.tesId,
      };
      setValue("tes", tesObj);
      existingDefaultValues.tes = tesObj;
    }
    if (assignment.details.tesSelections) {
      const tesVariation = assignment.details.tesSelections;
      setValue("tesVariation", tesVariation);
      existingDefaultValues.tesVariation = tesVariation;
    }

    setDefaultValues({ ...existingDefaultValues });
  }, [assignment.details, translatedTesNames, setValue]);

  useEffect(() => {
    if (tes?.id) {
      setTesVariationsLoading(true);
      fetchTESVariations(tes.id, today("yyyy-MM-dd")).then((res) => {
        setElements([...res.elements]);
        setTesVariationsLoading(false);
      });
    }
  }, [tes?.id]);

  useEffect(() => {
    if (successfulSubmit) {
      sendNotification(
        t("talentProfile:checkTesChangesTitle"),
        t("talentProfile:checkTesChangesText"),
      );
      setSuccesfulSubmit(undefined);
      // reset isDirty after successful submit
      resetForm({}, { keepValues: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfulSubmit, resetForm, t]);

  const onSave = (fields: FieldValues) => {
    const payload = {
      assignmentId: assignment.assignmentId,
      version: assignment.details.version,
      tesSelections: fields.tesVariation,
    };
    dispatch(changeAssignmentTesSel(payload)).then((action) => {
      if (isFulfilled(action)) {
        setEdit(false);
        setSuccesfulSubmit(true);
      }
    });
  };

  const confirmEdit = () => {
    setEdit(true);
    setIsConfirmEditDialogOpen(false);
  };

  const onEdit = () => {
    // Ask for confirmation for long assignments
    if (!edit && !assignment.details.isShift) setIsConfirmEditDialogOpen(true);
    else if (!edit) setEdit(true);
    setReset(false);

    if (edit) handleSubmit(onSave)();
  };

  const onCancel = () => {
    resetForm(defaultValues);
    setReset(true);
    setEdit(false);
  };

  return (
    <FormSection
      title={t("talentProfile:tesContract")}
      edit={edit}
      onEdit={onEdit}
      onCancel={onCancel}
      saveDisabled={!isValidTesVariations || !isDirty || !isEmpty(errors)}
      editDisabled={tesVariationsLoading}
      readOnly={!canEdit}
    >
      {tesVariationsLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Grid
            container
            item
            xs={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="right" color="textSecondary">
              {t("common:TES")}
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <Controller
              name="tes"
              control={control}
              render={({ field }) => (
                <CommonTextField
                  {...field}
                  disabled
                  select
                  size="small"
                  fullWidth
                  value={tes?.id}
                  sx={{ maxWidth: "245px" }}
                >
                  <MenuItem value={tes?.id}>{tes?.name}</MenuItem>
                </CommonTextField>
              )}
            />
          </Grid>
          <Controller
            name="tesVariation"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <TesVariationForm
                reset={reset}
                defaultValues={value}
                elements={elements}
                labelGridProps={{ xs: 2 }}
                fieldGridProps={{ xs: 10 }}
                fieldProps={{
                  sx: {
                    maxWidth: "245px",
                    backgroundColor: "#F9F9FA99",
                  },
                }}
                disabled={!edit}
                onChange={(newValue, isValid) => {
                  setIsValidTesVariations(isValid);
                  onChange(newValue);
                }}
              />
            )}
          />
        </>
      )}
      <ConfirmationDialog
        title={t("common:doYouWantToEdit")}
        contentText={
          <Box>
            <Typography>{t("talentProfile:variationChangeWarning")}</Typography>
            <Typography fontWeight={700}>
              {t("talentProfile:ifUnsureContactSupport")}
            </Typography>
          </Box>
        }
        confirmText={t("common:continue")}
        cancelText={t("common:cancel")}
        onConfirm={confirmEdit}
        onCancel={() => setIsConfirmEditDialogOpen(false)}
        isOpen={isConfirmEditDialogOpen}
        sx={{
          maxWidth: "444px",
          backgroundColor: "#fff",
        }}
        titleSx={{ color: theme.palette.error.main }}
      />
    </FormSection>
  );
};

export default TESContract;
